import { type FC, useState } from "react";
import { Link } from "react-router-dom";
import { PageTitle } from "../../../components/PageTitle";
import {
  type AdminCategoryUsage,
  useAdminLLMSummaryApi,
  useAdminMatchingStatsApi,
  useAdminUsageOverviewApi,
} from "../../../hooks/useAdminApi";

/**
 * カテゴリ利用率バッジ
 * @param {{ cat: AdminCategoryUsage }} props カテゴリ
 * @return {JSX.Element} バッジ
 */
const UsageBadge: FC<{ cat: AdminCategoryUsage }> = ({ cat }) => {
  if (cat.limit === 0) return <span className="text-gray-400">-</span>;

  const pct = Math.round(cat.usageRate * 100);
  const isExceeded = cat.used >= cat.limit;
  const color = isExceeded
    ? "badge-error"
    : cat.isWarning
      ? "badge-warning"
      : "badge-ghost";

  return (
    <span className={`badge badge-sm ${color}`}>
      {cat.used}/{cat.limit} ({pct}%)
    </span>
  );
};

/**
 * 管理者向け利用状況ダッシュボードページ
 * @return {JSX.Element} ダッシュボードページ
 */
export const UsageDashboardPage: FC = () => {
  const [page, setPage] = useState(1);
  const {
    data: overview,
    loading: overviewLoading,
    error: overviewError,
  } = useAdminUsageOverviewApi({ page, limit: 20 });
  const { data: llmSummary, loading: llmLoading } = useAdminLLMSummaryApi();
  const { data: matchingStats, loading: matchingLoading } =
    useAdminMatchingStatsApi();

  return (
    <div className="container mx-auto px-4 py-6">
      <div className="flex justify-between items-center mb-6">
        <PageTitle>利用状況ダッシュボード</PageTitle>
        <Link to="/admin" className="btn btn-ghost btn-sm">
          ← 管理画面に戻る
        </Link>
      </div>

      {/* テナント一覧テーブル */}
      <div className="card bg-base-100 shadow-md mb-6">
        <div className="card-body">
          <h2 className="card-title text-color-title">テナント別利用状況</h2>

          {overviewError && (
            <div className="alert alert-error mb-4">
              <span>エラー: {overviewError.message}</span>
            </div>
          )}

          {overviewLoading ? (
            <div className="flex justify-center py-8">
              <span className="loading loading-spinner loading-lg"></span>
            </div>
          ) : overview && overview.items.length > 0 ? (
            <>
              <div className="overflow-x-auto">
                <table className="table">
                  <thead>
                    <tr>
                      <th>テナント名</th>
                      <th>プラン</th>
                      <th>ステータス</th>
                      <th className="text-right">メール</th>
                      <th className="text-right">マッチング</th>
                      <th className="text-right">配信</th>
                      <th className="text-right">AI</th>
                    </tr>
                  </thead>
                  <tbody>
                    {overview.items.map(item => (
                      <tr key={item.tenantId} className="hover">
                        <td>
                          <Link
                            to={`/admin/tenants/${item.tenantId}`}
                            className="link link-primary"
                          >
                            {item.tenantName}
                          </Link>
                        </td>
                        <td>{item.planName}</td>
                        <td>
                          <span
                            className={`badge badge-sm ${
                              item.status === "active"
                                ? "badge-success"
                                : item.status === "trial"
                                  ? "badge-info"
                                  : "badge-ghost"
                            }`}
                          >
                            {item.status}
                          </span>
                        </td>
                        {item.categories.map(cat => (
                          <td key={cat.category} className="text-right">
                            <UsageBadge cat={cat} />
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* ページネーション */}
              {overview.total > overview.limit && (
                <div className="flex justify-center mt-4">
                  <div className="join">
                    <button
                      className="join-item btn btn-sm"
                      disabled={page <= 1}
                      onClick={() => setPage(p => Math.max(1, p - 1))}
                    >
                      前へ
                    </button>
                    <button className="join-item btn btn-sm btn-disabled">
                      {page} / {Math.ceil(overview.total / overview.limit)}
                    </button>
                    <button
                      className="join-item btn btn-sm"
                      disabled={
                        page >= Math.ceil(overview.total / overview.limit)
                      }
                      onClick={() => setPage(p => p + 1)}
                    >
                      次へ
                    </button>
                  </div>
                </div>
              )}
            </>
          ) : (
            <p className="text-gray-500 text-center py-4">
              アクティブな契約を持つテナントがありません
            </p>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* LLMコストサマリー */}
        <div className="card bg-base-100 shadow-md">
          <div className="card-body">
            <h2 className="card-title text-color-title">
              LLMコストサマリー（過去30日）
            </h2>

            {llmLoading ? (
              <div className="flex justify-center py-4">
                <span className="loading loading-spinner loading-md"></span>
              </div>
            ) : llmSummary ? (
              <>
                <div className="stat bg-base-200 rounded-lg mb-4">
                  <div className="stat-title">総コスト</div>
                  <div className="stat-value text-primary text-2xl">
                    ${llmSummary.totalCostUsd.toFixed(4)}
                  </div>
                </div>

                {llmSummary.models.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="table table-sm">
                      <thead>
                        <tr>
                          <th>モデル</th>
                          <th>操作</th>
                          <th className="text-right">回数</th>
                          <th className="text-right">トークン</th>
                          <th className="text-right">コスト</th>
                        </tr>
                      </thead>
                      <tbody>
                        {llmSummary.models.flatMap(model =>
                          model.operations.map(op => (
                            <tr key={`${model.model}-${op.operation}`}>
                              <td className="text-xs">{model.model}</td>
                              <td className="text-xs">{op.operation}</td>
                              <td className="text-right">
                                {op.callCount.toLocaleString()}
                              </td>
                              <td className="text-right">
                                {op.totalTokens.toLocaleString()}
                              </td>
                              <td className="text-right">
                                ${op.estimatedCostUsd.toFixed(4)}
                              </td>
                            </tr>
                          )),
                        )}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="text-gray-500 text-center py-2">データなし</p>
                )}
              </>
            ) : null}
          </div>
        </div>

        {/* マッチング統計 */}
        <div className="card bg-base-100 shadow-md">
          <div className="card-body">
            <h2 className="card-title text-color-title">
              マッチング統計（過去30日）
            </h2>

            {matchingLoading ? (
              <div className="flex justify-center py-4">
                <span className="loading loading-spinner loading-md"></span>
              </div>
            ) : matchingStats ? (
              <>
                <div className="stat bg-base-200 rounded-lg mb-4">
                  <div className="stat-title">総マッチング数</div>
                  <div className="stat-value text-primary text-2xl">
                    {matchingStats.total.toLocaleString()}
                  </div>
                </div>

                {matchingStats.byType.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="table table-sm">
                      <thead>
                        <tr>
                          <th>種別</th>
                          <th className="text-right">回数</th>
                          <th className="text-right">割合</th>
                        </tr>
                      </thead>
                      <tbody>
                        {matchingStats.byType.map(t => (
                          <tr key={t.triggerType}>
                            <td>{t.triggerType}</td>
                            <td className="text-right">
                              {t.count.toLocaleString()}
                            </td>
                            <td className="text-right">{t.pct}%</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="text-gray-500 text-center py-2">データなし</p>
                )}
              </>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
};

export default UsageDashboardPage;
